import type { DispatchStopStatus, LoadingItem } from "@kitchen/schemas";
import { Alert, Button, Chip, faDigits, Field, IconButton, Panel, Seg } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, distributionApi, type DispatchRow, type StaffRow } from "../../api.js";
import { addDays, tehranToday } from "../../labels.js";
import { ErrorLine, useAction } from "../common.js";
import { useMeals, useOrgs } from "../menu/data.js";

const STOP_STATUS_LABELS: Record<DispatchStopStatus, { label: string; tone?: "ok" | "coral" | "warn" }> = {
  pending: { label: "در انتظار" },
  "on-route": { label: "در مسیر", tone: "warn" },
  delivered: { label: "تحویل کامل", tone: "ok" },
  "delivered-short": { label: "تحویل با کسری", tone: "warn" },
  failed: { label: "عدم تحویل", tone: "coral" },
};

async function printSlips(
  siteId: string,
  date: string,
  mealId: string,
  kind: "driver-sheet" | "container-label",
  ref?: string,
): Promise<number> {
  const { ids } = await api<{ ids: string[] }>("/printing/jobs", {
    body: { siteId, date, mealId, kind, ref },
  });
  const listed = await api<{ id: string; version: number }[]>(
    `/printing/jobs?siteId=${siteId}&date=${date}&mealId=${mealId}`,
  );
  for (const id of ids) {
    const { html } = await api<{ html: string }>(`/printing/jobs/${id}`);
    await new Promise<void>((resolve) => {
      const frame = document.createElement("iframe");
      frame.style.position = "fixed";
      frame.style.width = "0";
      frame.style.height = "0";
      frame.style.border = "0";
      frame.srcdoc = html;
      frame.onload = () => {
        frame.contentWindow?.print();
        setTimeout(() => {
          frame.remove();
          resolve();
        }, 500);
      };
      document.body.appendChild(frame);
    });
    const version = listed.find((j) => j.id === id)?.version ?? 0;
    await api(`/printing/jobs/${id}/printed`, { body: { version } }).catch(() => undefined);
  }
  return ids.length;
}

export function DispatchTab() {
  const meals = useMeals();
  const orgs = useOrgs();
  const staff = useQuery({ queryKey: ["staff"], queryFn: () => api<StaffRow[]>("/staff") });
  const sites = useQuery({
    queryKey: ["sites"],
    queryFn: () => api<{ id: string; name: string }[]>("/sites"),
  });

  const [date, setDate] = useState(tehranToday());
  const liveMeals = (meals.data ?? []).filter((m) => m.active);
  const [selectedMealId, setSelectedMealId] = useState("");
  const activeMealId = selectedMealId || liveMeals[0]?.id || "";

  const dispatches = useQuery({
    queryKey: ["distribution", "dispatches", date, activeMealId],
    queryFn: () => distributionApi.listDispatches(date, activeMealId || undefined),
    enabled: Boolean(date),
  });

  const routes = useQuery({
    queryKey: ["distribution", "routes", activeMealId],
    queryFn: () => distributionApi.listRoutes(activeMealId || undefined),
    enabled: Boolean(activeMealId),
  });

  const problems = useQuery({
    queryKey: ["distribution", "problems", date],
    queryFn: () => distributionApi.listProblems(date),
    enabled: Boolean(date),
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [swapDriverTarget, setSwapDriverTarget] = useState<DispatchRow | null>(null);
  const [reassignStopTarget, setReassignStopTarget] = useState<{
    dispatch: DispatchRow;
    stopId: string;
    unitName: string;
  } | null>(null);
  const [viewReportId, setViewReportId] = useState<string | null>(null);

  const staffMap = new Map((staff.data ?? []).map((s) => [s.id, s.displayName]));
  const unitMap = new Map(orgs.units.map((u) => [u.id, u.name]));
  const routeMap = new Map((routes.data ?? []).map((r) => [r.id, r.name]));

  const confirmLoading = useAction(
    async ({ dispatchId, version }: { dispatchId: string; version: number }) => {
      await distributionApi.confirmLoading(dispatchId, version);
    },
    [["distribution", "dispatches"]],
  );

  const printDriverManifest = useAction(
    async ({ dispatchId, siteId }: { dispatchId: string; siteId: string }) => {
      const targetSiteId = siteId || (sites.data ?? [])[0]?.id || "";
      if (!targetSiteId) throw new Error("هیچ آشپزخانه یا محلی یافت نشد");
      await printSlips(targetSiteId, date, activeMealId, "driver-sheet", dispatchId);
    },
    [],
  );

  const printContainerLabels = useAction(
    async ({ dispatchId, siteId }: { dispatchId: string; siteId: string }) => {
      const targetSiteId = siteId || (sites.data ?? [])[0]?.id || "";
      if (!targetSiteId) throw new Error("هیچ آشپزخانه یا محلی یافت نشد");
      await printSlips(targetSiteId, date, activeMealId, "container-label", dispatchId);
    },
    [],
  );

  const moveStop = useAction(
    async ({
      dispatchId,
      stopId,
      version,
      newSequence,
    }: {
      dispatchId: string;
      stopId: string;
      version: number;
      newSequence: number;
    }) => {
      await distributionApi.moveStop(dispatchId, stopId, version, newSequence);
    },
    [["distribution", "dispatches"]],
  );

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Controls Bar */}
      <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "end" }}>
        <Field
          id="dispatch-date"
          label="تاریخ توزیع"
          type="date"
          value={date}
          onChange={(e) => {
            if (e.target.value) setDate(e.target.value);
          }}
        />
        <Button
          variant="ghost"
          onClick={() => {
            setDate(tehranToday());
          }}
        >
          امروز
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setDate(addDays(tehranToday(), 1));
          }}
        >
          فردا
        </Button>

        {liveMeals.length > 0 && (
          <Seg
            label="وعده غذایی"
            value={activeMealId}
            onChange={setSelectedMealId}
            options={liveMeals.map((m) => ({ value: m.id, label: m.name }))}
          />
        )}

        <span className="grow" />

        <Button
          variant="primary"
          icon="plus"
          onClick={() => {
            setShowCreateModal(true);
          }}
        >
          ایجاد سفر از قالب مسیر
        </Button>
      </div>

      {/* Dispatches List */}
      <div className="stack" style={{ gap: "var(--s3)" }}>
        {dispatches.isLoading && <p className="muted">در حال بارگذاری سفرهای توزیع...</p>}
        {dispatches.data && dispatches.data.length === 0 && (
          <Panel title="سفرهای توزیع امروز">
            <p className="ink2">
              هنوز هیچ سفری برای تاریخ {date} و وعده انتخاب‌شده ایجاد نشده است. برای شروع، «ایجاد سفر از قالب
              مسیر» را بزنید.
            </p>
          </Panel>
        )}

        {dispatches.data &&
          dispatches.data.map((dispatch) => {
            const routeName = dispatch.templateId
              ? (routeMap.get(dispatch.templateId) ?? "سفر توزیع")
              : "سفر توزیع";
            const driverName = staffMap.get(dispatch.driverId) ?? "راننده";
            const isCompleted = dispatch.stops.every(
              (s) => s.status === "delivered" || s.status === "delivered-short" || s.status === "failed",
            );
            const hasMultipleLoadingPoints = dispatch.loadingWarnings.length > 0;

            // Group loading items by packaging site
            const packingSiteMap = new Map<
              string,
              { siteName: string; total: number; items: LoadingItem[] }
            >();
            for (const stop of dispatch.stops) {
              for (const li of stop.loadingItems) {
                const sName = li.packagingSiteName ?? "آشپزخانه مرکزی";
                const existing = packingSiteMap.get(li.packagingSiteId) ?? {
                  siteName: sName,
                  total: 0,
                  items: [],
                };
                existing.total += li.orderedQuantity;
                existing.items.push(li);
                packingSiteMap.set(li.packagingSiteId, existing);
              }
            }

            return (
              <Panel
                key={dispatch.id}
                title={
                  <div className="row" style={{ gap: "var(--s3)", alignItems: "center" }}>
                    <span>{routeName}</span>
                    <Chip tone={isCompleted ? "ok" : "warn"}>
                      {isCompleted ? "توزیع کامل شد" : "در حال انجام"}
                    </Chip>
                  </div>
                }
                actions={
                  <div className="row" style={{ gap: "var(--s2)" }}>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setSwapDriverTarget(dispatch);
                      }}
                    >
                      تغییر راننده ({driverName})
                    </Button>
                    {!dispatch.startedAt && (
                      <Button
                        variant="primary"
                        onClick={() => {
                          confirmLoading.run({ dispatchId: dispatch.id, version: dispatch.version });
                        }}
                        disabled={confirmLoading.busy}
                      >
                        بارگیری شد
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      icon="printer"
                      onClick={() => {
                        const defaultSiteId =
                          Array.from(packingSiteMap.keys())[0] || (sites.data ?? [])[0]?.id || "";
                        printDriverManifest.run({ dispatchId: dispatch.id, siteId: defaultSiteId });
                      }}
                      disabled={printDriverManifest.busy}
                    >
                      چاپ برگه راننده
                    </Button>
                    <Button
                      variant="ghost"
                      icon="printer"
                      onClick={() => {
                        setViewReportId(dispatch.id);
                      }}
                    >
                      گزارش سفر
                    </Button>
                  </div>
                }
              >
                <div className="stack" style={{ gap: "var(--s3)" }}>
                  <div className="row wrap muted" style={{ gap: "var(--s4)", fontSize: 14 }}>
                    <div>
                      راننده: <strong>{driverName}</strong>
                    </div>
                    {dispatch.startedAt && (
                      <div>زمان شروع بارگیری: {new Date(dispatch.startedAt).toLocaleTimeString("fa-IR")}</div>
                    )}
                  </div>

                  {/* Multiple Loading Points Warning */}
                  {hasMultipleLoadingPoints && (
                    <Alert
                      level="important"
                      icon="alert"
                      title="توجه: بارگیری از چند آشپزخانه / محل انجام می‌شود"
                    >
                      غذاهای این مسیر از چند محل مختلف بسته‌بندی یا طبخ می‌شوند. لطفاً دقت کنید راننده کلیه
                      اقلام را بارگیری کند.
                    </Alert>
                  )}

                  {/* Loading Manifest Summary */}
                  {packingSiteMap.size > 0 && (
                    <div className="card" style={{ padding: "var(--s3)", background: "var(--panel)" }}>
                      <div
                        className="row wrap justify-between items-center"
                        style={{ marginBottom: "var(--s2)" }}
                      >
                        <div className="b" style={{ fontSize: 13 }}>
                          خلاصه مانیفست بارگیری:
                        </div>
                        <Button
                          variant="default"
                          icon="printer"
                          onClick={() => {
                            const defaultSiteId =
                              Array.from(packingSiteMap.keys())[0] || (sites.data ?? [])[0]?.id || "";
                            printContainerLabels.run({ dispatchId: dispatch.id, siteId: defaultSiteId });
                          }}
                          disabled={printContainerLabels.busy}
                        >
                          چاپ برچسب‌های ظروف
                        </Button>
                      </div>
                      <div className="row wrap" style={{ gap: "var(--s3)" }}>
                        {Array.from(packingSiteMap.entries()).map(([siteId, ps]) => (
                          <div
                            key={siteId}
                            style={{
                              padding: "var(--s2) var(--s3)",
                              background: "var(--card)",
                              borderRadius: "var(--r-control)",
                              border: "1px solid var(--card-line)",
                            }}
                          >
                            <div className="muted" style={{ fontSize: 12 }}>
                              محل بارگیری: {ps.siteName}
                            </div>
                            <div className="b" style={{ marginTop: 4 }}>
                              مجموع: {faDigits(ps.total)} پرس
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Stops Table */}
                  <div className="card" style={{ padding: "var(--s2) var(--s3)" }}>
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th style={{ width: 44 }}>ترتیب</th>
                          <th>واحد تحویل‌گیرنده</th>
                          <th>وضعیت تحویل</th>
                          <th>تعداد پرس</th>
                          <th>ظروف امانی</th>
                          <th style={{ width: 100 }}>جابجایی</th>
                          <th style={{ width: 130 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {dispatch.stops.map((stop, idx) => {
                          const stopSt = STOP_STATUS_LABELS[stop.status];
                          const unitName = unitMap.get(stop.unitId) ?? stop.unitId;
                          const plannedCount = stop.loadingItems.reduce(
                            (acc, li) => acc + li.orderedQuantity,
                            0,
                          );
                          const deliveredCount = stop.deliveryResult
                            ? stop.deliveryResult.deliveredLines.reduce(
                                (acc, dl) => acc + dl.deliveredQuantity,
                                0,
                              )
                            : null;

                          return (
                            <tr key={stop.id}>
                              <td className="num b">{faDigits(stop.sequence)}</td>
                              <td className="m b">{unitName}</td>
                              <td>
                                <Chip tone={stopSt.tone}>{stopSt.label}</Chip>
                                {stop.deliveryResult?.failureReason && (
                                  <div className="hint" style={{ color: "var(--coral-ink)" }}>
                                    {stop.deliveryResult.failureReason}
                                  </div>
                                )}
                              </td>
                              <td className="num">
                                {deliveredCount !== null ? (
                                  <span
                                    style={{
                                      color: deliveredCount < plannedCount ? "var(--warn-ink)" : undefined,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {faDigits(deliveredCount)} از {faDigits(plannedCount)}
                                  </span>
                                ) : (
                                  `${faDigits(plannedCount)} پرس`
                                )}
                              </td>
                              <td className="num">
                                {stop.deliveryResult ? (
                                  <span>
                                    {Object.values(stop.deliveryResult.containersDelivered ?? {}).reduce(
                                      (a, b) => a + b,
                                      0,
                                    )}{" "}
                                    رفت /{" "}
                                    {Object.values(stop.deliveryResult.containersReturned ?? {}).reduce(
                                      (a, b) => a + b,
                                      0,
                                    )}{" "}
                                    برگشت
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td>
                                <div className="row" style={{ gap: "var(--s1)" }}>
                                  <IconButton
                                    label="بالا"
                                    icon="up"
                                    disabled={idx === 0 || moveStop.busy}
                                    onClick={() => {
                                      moveStop.run({
                                        dispatchId: dispatch.id,
                                        stopId: stop.id,
                                        version: dispatch.version,
                                        newSequence: stop.sequence - 1,
                                      });
                                    }}
                                  />
                                  <IconButton
                                    label="پایین"
                                    icon="down"
                                    disabled={idx === dispatch.stops.length - 1 || moveStop.busy}
                                    onClick={() => {
                                      moveStop.run({
                                        dispatchId: dispatch.id,
                                        stopId: stop.id,
                                        version: dispatch.version,
                                        newSequence: stop.sequence + 1,
                                      });
                                    }}
                                  />
                                </div>
                              </td>
                              <td className="end">
                                <Button
                                  variant="ghost"
                                  onClick={() => {
                                    setReassignStopTarget({
                                      dispatch,
                                      stopId: stop.id,
                                      unitName,
                                    });
                                  }}
                                >
                                  انتقال به سفر دیگر
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Panel>
            );
          })}
      </div>

      {/* Failed Deliveries & Problems Section */}
      {problems.data && problems.data.length > 0 && (
        <Panel title="مشکلات و عدم تحویل‌های ثبت‌شده">
          <div className="card" style={{ padding: "var(--s2) var(--s4)" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>نوع مشکل</th>
                  <th>واحد / ایستگاه</th>
                  <th>توضیحات</th>
                  <th>وضعیت</th>
                </tr>
              </thead>
              <tbody>
                {problems.data.map((p) => (
                  <tr key={p.id}>
                    <td className="b" style={{ color: "var(--coral-ink)" }}>
                      {p.kind}
                    </td>
                    <td>{unitMap.get(p.unitId) ?? p.unitId}</td>
                    <td>{p.note}</td>
                    <td>
                      {p.resolvedAt ? (
                        <Chip tone="ok">رفع شد: {p.resolutionNote}</Chip>
                      ) : (
                        <Chip tone="coral">در دست بررسی</Chip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateDispatchModal
          businessDate={date}
          mealId={activeMealId}
          templates={routes.data ?? []}
          onClose={() => {
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Swap Driver Modal */}
      {swapDriverTarget && (
        <SwapDriverModal
          dispatch={swapDriverTarget}
          staff={staff.data ?? []}
          onClose={() => {
            setSwapDriverTarget(null);
          }}
        />
      )}

      {/* Reassign Stop Modal */}
      {reassignStopTarget && (
        <ReassignStopModal
          sourceDispatch={reassignStopTarget.dispatch}
          stopId={reassignStopTarget.stopId}
          unitName={reassignStopTarget.unitName}
          allDispatches={dispatches.data ?? []}
          onClose={() => {
            setReassignStopTarget(null);
          }}
        />
      )}

      {/* Report View Modal */}
      {viewReportId && (
        <DispatchReportModal
          dispatchId={viewReportId}
          onClose={() => {
            setViewReportId(null);
          }}
        />
      )}
    </div>
  );
}

function CreateDispatchModal({
  businessDate,
  mealId,
  templates,
  onClose,
}: {
  businessDate: string;
  mealId: string;
  templates: { id: string; name: string; mealId: string; active: boolean }[];
  onClose: () => void;
}) {
  const activeTemplates = templates.filter((t) => t.mealId === mealId && t.active);
  const [templateId, setTemplateId] = useState(activeTemplates[0]?.id ?? "");

  const create = useAction(async () => {
    if (!templateId) return;
    await distributionApi.createDispatchFromTemplate(templateId, businessDate);
    onClose();
  }, [["distribution", "dispatches"]]);

  return (
    <Panel title="ایجاد سفر توزیع از قالب مسیر">
      <form
        className="stack"
        style={{ gap: "var(--s3)" }}
        onSubmit={(e) => {
          e.preventDefault();
          create.run(undefined);
        }}
      >
        <p className="ink2">
          سفارش‌های تأییدشده مربوط به این تاریخ و وعده به‌صورت خودکار بر اساس ایستگاه‌های مسیر الصاق خواهند
          شد.
        </p>

        <div className="field">
          <label htmlFor="select-template">انتخاب قالب مسیر</label>
          <select
            id="select-template"
            className="input"
            value={templateId}
            onChange={(e) => {
              setTemplateId(e.target.value);
            }}
            required
          >
            {activeTemplates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <ErrorLine error={create.error} />

        <div className="row" style={{ gap: "var(--s2)", justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onClose} disabled={create.busy}>
            انصراف
          </Button>
          <Button variant="primary" type="submit" disabled={create.busy || !templateId}>
            {create.busy ? "در حال ایجاد..." : "تأیید و ایجاد سفر"}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function SwapDriverModal({
  dispatch,
  staff,
  onClose,
}: {
  dispatch: DispatchRow;
  staff: StaffRow[];
  onClose: () => void;
}) {
  const [newDriverId, setNewDriverId] = useState(dispatch.driverId);

  const swap = useAction(async () => {
    await distributionApi.swapDriver(dispatch.id, dispatch.version, newDriverId);
    onClose();
  }, [["distribution", "dispatches"]]);

  return (
    <Panel title="تغییر راننده برای سفر توزیع">
      <form
        className="stack"
        style={{ gap: "var(--s3)" }}
        onSubmit={(e) => {
          e.preventDefault();
          swap.run(undefined);
        }}
      >
        <div className="field">
          <label htmlFor="new-driver">انتخاب راننده جدید</label>
          <select
            id="new-driver"
            className="input"
            value={newDriverId}
            onChange={(e) => {
              setNewDriverId(e.target.value);
            }}
            required
          >
            <option value="">انتخاب راننده...</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>
                {s.displayName}
              </option>
            ))}
          </select>
        </div>

        <ErrorLine error={swap.error} />

        <div className="row" style={{ gap: "var(--s2)", justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onClose} disabled={swap.busy}>
            انصراف
          </Button>
          <Button variant="primary" type="submit" disabled={swap.busy || !newDriverId}>
            {swap.busy ? "در حال ثبت..." : "ثبت تغییر راننده"}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function ReassignStopModal({
  sourceDispatch,
  stopId,
  unitName,
  allDispatches,
  onClose,
}: {
  sourceDispatch: DispatchRow;
  stopId: string;
  unitName: string;
  allDispatches: DispatchRow[];
  onClose: () => void;
}) {
  const otherDispatches = allDispatches.filter((d) => d.id !== sourceDispatch.id);
  const [targetDispatchId, setTargetDispatchId] = useState(otherDispatches[0]?.id ?? "");

  const reassign = useAction(async () => {
    if (!targetDispatchId) return;
    await distributionApi.reassignStop(sourceDispatch.id, stopId, sourceDispatch.version, targetDispatchId);
    onClose();
  }, [["distribution", "dispatches"]]);

  return (
    <Panel title={`انتقال ایستگاه «${unitName}» به سفر دیگر`}>
      <form
        className="stack"
        style={{ gap: "var(--s3)" }}
        onSubmit={(e) => {
          e.preventDefault();
          reassign.run(undefined);
        }}
      >
        {otherDispatches.length === 0 ? (
          <p className="ink2">سفر دیگری برای این تاریخ و وعده وجود ندارد که ایستگاه به آن منتقل شود.</p>
        ) : (
          <div className="field">
            <label htmlFor="target-dispatch">سفر مقصد</label>
            <select
              id="target-dispatch"
              className="input"
              value={targetDispatchId}
              onChange={(e) => {
                setTargetDispatchId(e.target.value);
              }}
              required
            >
              {otherDispatches.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.stops.length} ایستگاه فعلی (راننده: {d.driverId})
                </option>
              ))}
            </select>
          </div>
        )}

        <ErrorLine error={reassign.error} />

        <div className="row" style={{ gap: "var(--s2)", justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onClose} disabled={reassign.busy}>
            انصراف
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={reassign.busy || !targetDispatchId || otherDispatches.length === 0}
          >
            {reassign.busy ? "در حال انتقال..." : "تأیید انتقال ایستگاه"}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function DispatchReportModal({ dispatchId, onClose }: { dispatchId: string; onClose: () => void }) {
  const orgs = useOrgs();
  const unitMap = new Map(orgs.units.map((u) => [u.id, u.name]));

  const report = useQuery({
    queryKey: ["distribution", "report", dispatchId],
    queryFn: () => distributionApi.getReport(dispatchId),
  });

  return (
    <Panel title="گزارش عملکرد روز راننده">
      {report.isLoading && <p className="muted">در حال دریافت اطلاعات گزارش...</p>}
      {report.data && (
        <div className="stack" style={{ gap: "var(--s3)" }}>
          <div className="card" style={{ padding: "var(--s3)" }}>
            <div
              className="grid"
              style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "var(--s2)" }}
            >
              <div>
                تاریخ: <strong>{report.data.businessDate}</strong>
              </div>
              <div>
                ایستگاه‌های انجام‌شده:{" "}
                <strong>
                  {faDigits(report.data.completedStops)} از {faDigits(report.data.totalStops)}
                </strong>
              </div>
              <div>
                پرس‌های تحویل‌شده:{" "}
                <strong>
                  {faDigits(report.data.totalDeliveredPortions)} از{" "}
                  {faDigits(report.data.totalOrderedPortions)}
                </strong>
              </div>
              <div>
                کسری غذا: <strong>{faDigits(report.data.shortPortions)} پرس</strong>
              </div>
            </div>
          </div>

          {report.data.failures.length > 0 && (
            <div className="stack" style={{ gap: "var(--s2)" }}>
              <div className="b" style={{ color: "var(--coral-ink)" }}>
                موارد عدم تحویل:
              </div>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>واحد</th>
                    <th>علت</th>
                    <th>زمان</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.failures.map((f, i) => (
                    <tr key={i}>
                      <td className="b">{unitMap.get(f.unitId) ?? f.unitId}</td>
                      <td>{f.reason}</td>
                      <td>{new Date(f.deliveredAt).toLocaleTimeString("fa-IR")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="row" style={{ justifyContent: "flex-end", marginTop: "var(--s2)" }}>
            <Button variant="primary" onClick={onClose}>
              بستن
            </Button>
          </div>
        </div>
      )}
    </Panel>
  );
}
